import { useQuery } from '@tanstack/react-query';

import { useSession } from '@/compositions/hooks/useSession';
import { accountAccessKeys } from '@/services/account-access/account-access.keys';
import { accountAccessService } from '@/services/account-access/account-access.service';

interface UseAccountAccess {
  isAccountSuspended: boolean;
  suspendedInstitutionName: string | null;
  /** `true` enquanto a resposta não chegou — não decida nada nesse intervalo. */
  isLoading: boolean;
}

/**
 * Esta conta ainda pode usar o app — e, se não puder, por quê.
 *
 * Mora em `compositions/` porque tem consumidores em módulos diferentes: o
 * guard `RequireActiveAccount` (`modules/auth`) e o aviso de instituição
 * bloqueada na tela de instituições (`modules/institutions`). É o mesmo motivo
 * de `useSession` e `usePlatformAdmin`.
 *
 * **Convidado nem pergunta**: sem sessão a RPC devolveria zero linhas de
 * qualquer jeito, e disparar a consulta só gastaria uma ida à rede na primeira
 * tela pública de quem não tem conta.
 *
 * ## `staleTime` curto e nova busca ao voltar o foco
 *
 * Isto é a "próxima verificação" prometida em D-05 do plano de painel do
 * admin: bloquear não derruba a sessão em silêncio, mas também não pode ficar
 * escondido atrás de um cache de 30s (o padrão global, `App.tsx`) enquanto a
 * pessoa continua usando o app bloqueado. `refetchOnWindowFocus` está
 * desligado globalmente por decisão de produto; esta consulta liga de volta só
 * para si, porque é exatamente o instante em que alguém pode ter acabado de
 * ser bloqueada — voltar de outra aba, ou o app voltar de segundo plano.
 *
 * ⚠️ **Não é autorização.** Quem barra é o banco; isto só explica o que a RLS
 * já decidiu, para a pessoa não achar que esqueceu a senha.
 */
export function useAccountAccess(): UseAccountAccess {
  const { session } = useSession();
  const profileId = session?.userId;

  const { data, isPending } = useQuery({
    queryKey: accountAccessKeys.state(profileId ?? 'guest'),
    queryFn: () => accountAccessService.myAccessState(),
    enabled: Boolean(profileId),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });

  return {
    isAccountSuspended: data?.isAccountSuspended ?? false,
    suspendedInstitutionName: data?.suspendedInstitutionName ?? null,
    // Sem sessão não há espera: a resposta é "não bloqueada" e é final.
    isLoading: Boolean(profileId) && isPending,
  };
}
